import React, { useState, useEffect, useMemo, useCallback } from 'react';

const RotatingText = ({
  texts = [],
  mainClassName = '',
  staggerFrom = 'first',
  staggerDuration = 0.025,
  splitLevelClassName = '',
  rotationInterval = 2000,
  splitBy = 'characters',
  prefix = 'Our'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Split text into characters
  const splitIntoCharacters = useCallback((text) => {
    return Array.from(text);
  }, []);

  // Create elements array for current text
  const createElements = useCallback((textIndex) => {
    const currentText = texts[textIndex] || '';
    if (splitBy === 'characters') {
      const words = currentText.split(' ');
      return words.map((word, i) => ({
        characters: splitIntoCharacters(word),
        needsSpace: i !== words.length - 1,
      }));
    }
    return currentText.split(' ').map((word, i, arr) => ({
      characters: [word],
      needsSpace: i !== arr.length - 1,
    }));
  }, [texts, splitBy, splitIntoCharacters]);

  const currentElements = useMemo(() => createElements(currentIndex), [createElements, currentIndex]);

  // Calculate stagger delay
  const getStaggerDelay = useCallback((index, totalChars) => {
    const total = totalChars;
    if (staggerFrom === 'first') return index * staggerDuration;
    if (staggerFrom === 'last') return (total - 1 - index) * staggerDuration;
    if (staggerFrom === 'center') {
      const center = Math.floor(total / 2);
      return Math.abs(center - index) * staggerDuration;
    }
    return index * staggerDuration;
  }, [staggerFrom, staggerDuration]);

  // Animation control
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      
      // Wait for exit animation, then switch
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % texts.length);
        setIsTransitioning(false);
      }, 600);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [texts.length, rotationInterval]);

  // Get total character count for stagger calculation
  const getTotalChars = (elements) => {
    return elements.reduce((sum, word) => sum + word.characters.length, 0);
  };

  // Render animated text
  const renderAnimatedText = (elements) => {
    const totalChars = getTotalChars(elements);
    
    return elements.map((wordObj, wordIndex) => {
      const previousCharsCount = elements
        .slice(0, wordIndex)
        .reduce((sum, word) => sum + word.characters.length, 0);
      
      return (
        <span key={`word-${wordIndex}`} className="inline-block">
          {wordObj.characters.map((char, charIndex) => {
            const globalCharIndex = previousCharsCount + charIndex;
            const delay = getStaggerDelay(globalCharIndex, totalChars);
            
            return (
              <span
                key={`char-${globalCharIndex}`}
                className="inline-block"
                style={{
                  transform: isTransitioning ? 'translateY(-100%) scale(0.8) translateZ(0)' : 'translateY(0%) scale(1) translateZ(0)',
                  opacity: isTransitioning ? 0 : 1,
                  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: `${delay}s`,
                  willChange: 'transform, opacity',
                }}
              >
                {char}
              </span>
            );
          })}
          {wordObj.needsSpace && <span className="inline-block">{"\u00A0"}</span>}
        </span>
      );
    });
  };

 return (
    <span className={`inline-flex items-baseline justify-center ${mainClassName}`}>
      {/* Prefix text inside the box */}
      {prefix && <span className="inline-block mr-2">{prefix}</span>}
      
      {/* Container for rotating text */}
      <span className="inline-block relative">
        {/* Screen reader text */}
        <span className="sr-only">{texts[currentIndex]}</span>
        
        {/* Animated text */}
        <span 
          aria-hidden="true" 
          className={`inline-block ${splitLevelClassName}`}
          style={{ 
            minHeight: '1em', // Maintain consistent height
            display: 'inline-flex',
            alignItems: 'baseline'
          }}
        >
          {renderAnimatedText(currentElements)}
        </span>
      </span>
    </span>
  )
}
export default RotatingText;